/**
 * Audit Trail System for Accounting Module
 *
 * Implements comprehensive audit logging for all accounting operations
 * as required by Italian accounting regulations (Art. 2214-2220 Codice Civile).
 *
 * Key features:
 * - Immutable append-only log
 * - Complete traceability of all financial operations
 * - Supports CREATE, UPDATE, DELETE, VIEW actions
 * - Entity types: INVOICE, PAYMENT, TRANSACTION
 * - Captures previous and new values for compliance
 * - Includes user identification and network context
 * - Export capability for audits
 *
 * IMPORTANT: This audit trail is immutable by design.
 * Entries cannot be modified or deleted to maintain integrity.
 */

// Use native crypto.randomUUID() instead of uuid package to avoid extra dependencies
// crypto.randomUUID() is available in Node.js 14.17+ and modern browsers
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older environments (unlikely but safe)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// ============================================================================
// TYPES
// ============================================================================

/**
 * Supported audit actions
 */
export type AuditAction = 'CREATE' | 'UPDATE' | 'DELETE' | 'VIEW';

/**
 * Entity types subject to audit
 */
export type AuditEntityType = 'INVOICE' | 'PAYMENT' | 'TRANSACTION';

/**
 * Complete audit entry structure
 */
export interface AuditEntry {
  /** Unique identifier for this audit entry */
  id: string;

  /** ISO 8601 timestamp when action occurred */
  timestamp: string;

  /** User ID who performed the action */
  userId: string;

  /** Type of action performed */
  action: AuditAction;

  /** Type of entity affected */
  entityType: AuditEntityType;

  /** ID of the affected entity */
  entityId: string;

  /** Previous state of the entity (for UPDATE and DELETE) */
  previousValue?: Record<string, any>;

  /** New state of the entity (for CREATE and UPDATE) */
  newValue?: Record<string, any>;

  /** IP address from which action was performed */
  ipAddress?: string;

  /** User agent string of the client */
  userAgent?: string;

  /** Optional additional context or notes */
  metadata?: Record<string, any>;
}

/**
 * Input for creating a new audit entry
 * (id and timestamp are auto-generated)
 */
export type AuditEntryInput = Omit<AuditEntry, 'id' | 'timestamp'>;

/**
 * Filter options for querying audit entries
 */
export interface AuditFilter {
  entityType?: AuditEntityType;
  entityId?: string;
  userId?: string;
  action?: AuditAction;
  fromDate?: string;
  toDate?: string;
}

/**
 * Storage interface for audit persistence
 * Allows future implementation of database/file storage
 */
export interface AuditStorage {
  /**
   * Append a new audit entry
   * @param entry The audit entry to store
   * @returns Promise resolving to the stored entry
   */
  append(entry: AuditEntry): Promise<AuditEntry>;

  /**
   * Query audit entries with optional filters
   * @param filter Filter criteria
   * @returns Promise resolving to matching entries
   */
  query(filter?: AuditFilter): Promise<AuditEntry[]>;

  /**
   * Get all audit entries
   * @returns Promise resolving to all entries
   */
  getAll(): Promise<AuditEntry[]>;

  /**
   * Clear all entries (use with extreme caution, primarily for testing)
   * @returns Promise resolving when cleared
   */
  clear(): Promise<void>;
}

// ============================================================================
// IN-MEMORY STORAGE IMPLEMENTATION
// ============================================================================

/**
 * Simple in-memory storage implementation
 *
 * NOTES:
 * - Data is volatile and lost on restart
 * - Suitable for development and testing
 * - Production should use persistent storage (database, file system)
 * - Maintains chronological order
 */
class InMemoryAuditStorage implements AuditStorage {
  private entries: AuditEntry[] = [];

  async append(entry: AuditEntry): Promise<AuditEntry> {
    // Entries are immutable once created
    const frozenEntry = Object.freeze({ ...entry });
    this.entries.push(frozenEntry as AuditEntry);
    return frozenEntry as AuditEntry;
  }

  async query(filter?: AuditFilter): Promise<AuditEntry[]> {
    if (!filter) {
      return [...this.entries];
    }

    return this.entries.filter(entry => {
      // Entity type filter
      if (filter.entityType && entry.entityType !== filter.entityType) {
        return false;
      }

      // Entity ID filter
      if (filter.entityId && entry.entityId !== filter.entityId) {
        return false;
      }

      // User ID filter
      if (filter.userId && entry.userId !== filter.userId) {
        return false;
      }

      // Action filter
      if (filter.action && entry.action !== filter.action) {
        return false;
      }

      // Date range filter
      if (filter.fromDate && entry.timestamp < filter.fromDate) {
        return false;
      }

      if (filter.toDate && entry.timestamp > filter.toDate) {
        return false;
      }

      return true;
    });
  }

  async getAll(): Promise<AuditEntry[]> {
    return [...this.entries];
  }

  async clear(): Promise<void> {
    this.entries = [];
  }
}

// ============================================================================
// AUDIT TRAIL SERVICE
// ============================================================================

/**
 * Main service for audit trail management
 *
 * This service provides:
 * - Centralized audit logging
 * - Query capabilities
 * - Export functionality
 * - Storage abstraction
 *
 * Usage:
 * ```typescript
 * const auditService = new AuditTrailService();
 *
 * // Log an invoice creation
 * await auditService.log({
 *   userId: 'usr-123',
 *   action: 'CREATE',
 *   entityType: 'INVOICE',
 *   entityId: 'inv-456',
 *   newValue: { ... },
 *   ipAddress: '192.168.1.1',
 *   userAgent: 'Mozilla/5.0...'
 * });
 *
 * // Query audit history for an invoice
 * const history = await auditService.getByEntity('INVOICE', 'inv-456');
 *
 * // Export audit log as CSV
 * const csv = await auditService.export();
 * ```
 */
export class AuditTrailService {
  private storage: AuditStorage;

  /**
   * Create a new audit trail service
   * @param storage Optional custom storage implementation (defaults to in-memory)
   */
  constructor(storage?: AuditStorage) {
    this.storage = storage || new InMemoryAuditStorage();
  }

  /**
   * Log a new audit entry
   *
   * @param entry Audit entry data (id and timestamp auto-generated)
   * @returns Promise resolving to the created audit entry
   */
  async log(entry: AuditEntryInput): Promise<AuditEntry> {
    const auditEntry: AuditEntry = {
      id: generateUUID(),
      timestamp: new Date().toISOString(),
      ...entry
    };

    // Validate required fields
    if (!auditEntry.userId) {
      throw new Error('userId is required for audit entry');
    }
    if (!auditEntry.entityId) {
      throw new Error('entityId is required for audit entry');
    }

    return await this.storage.append(auditEntry);
  }

  /**
   * Get all audit entries for a specific entity
   *
   * @param entityType Type of entity
   * @param entityId ID of the entity
   * @returns Promise resolving to array of audit entries in chronological order
   */
  async getByEntity(
    entityType: AuditEntityType,
    entityId: string
  ): Promise<AuditEntry[]> {
    return await this.storage.query({ entityType, entityId });
  }

  /**
   * Get all audit entries for a specific user
   *
   * @param userId User ID
   * @returns Promise resolving to array of audit entries in chronological order
   */
  async getByUser(userId: string): Promise<AuditEntry[]> {
    return await this.storage.query({ userId });
  }

  /**
   * Get audit entries within a date range
   *
   * @param fromDate Start date (ISO 8601 format)
   * @param toDate End date (ISO 8601 format)
   * @returns Promise resolving to array of audit entries in chronological order
   */
  async getByDateRange(fromDate: string, toDate: string): Promise<AuditEntry[]> {
    return await this.storage.query({ fromDate, toDate });
  }

  /**
   * Query audit entries with custom filters
   *
   * @param filter Filter criteria
   * @returns Promise resolving to array of matching audit entries
   */
  async query(filter: AuditFilter): Promise<AuditEntry[]> {
    return await this.storage.query(filter);
  }

  /**
   * Get all audit entries
   *
   * @returns Promise resolving to all audit entries in chronological order
   */
  async getAll(): Promise<AuditEntry[]> {
    return await this.storage.getAll();
  }

  /**
   * Export audit log as CSV format
   *
   * @returns Promise resolving to CSV string
   */
  async export(): Promise<string> {
    const entries = await this.storage.getAll();

    // CSV headers
    const headers = [
      'ID',
      'Timestamp',
      'User ID',
      'Action',
      'Entity Type',
      'Entity ID',
      'Previous Value',
      'New Value',
      'IP Address',
      'User Agent',
      'Metadata'
    ];

    // Build CSV rows
    const rows = entries.map(entry => [
      entry.id,
      entry.timestamp,
      entry.userId,
      entry.action,
      entry.entityType,
      entry.entityId,
      entry.previousValue ? JSON.stringify(entry.previousValue) : '',
      entry.newValue ? JSON.stringify(entry.newValue) : '',
      entry.ipAddress || '',
      entry.userAgent || '',
      entry.metadata ? JSON.stringify(entry.metadata) : ''
    ]);

    // Format as CSV with proper escaping
    const csvLines = [headers, ...rows].map(row =>
      row.map(cell => {
        // Escape quotes and wrap in quotes if contains comma, newline, or quote
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('\n') || cellStr.includes('"')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(',')
    );

    return csvLines.join('\n');
  }

  /**
   * Clear all audit entries
   *
   * WARNING: This operation is irreversible and should only be used
   * in testing environments. Production data should NEVER be cleared.
   *
   * @returns Promise resolving when cleared
   */
  async clear(): Promise<void> {
    console.warn('AuditTrailService: Clearing all audit entries. This should only be done in testing.');
    await this.storage.clear();
  }

  /**
   * Get audit statistics
   *
   * @returns Promise resolving to statistics object
   */
  async getStatistics(): Promise<{
    totalEntries: number;
    byAction: Record<AuditAction, number>;
    byEntityType: Record<AuditEntityType, number>;
    uniqueUsers: number;
    dateRange: { earliest: string; latest: string } | null;
  }> {
    const entries = await this.storage.getAll();

    const stats = {
      totalEntries: entries.length,
      byAction: {
        CREATE: 0,
        UPDATE: 0,
        DELETE: 0,
        VIEW: 0
      } as Record<AuditAction, number>,
      byEntityType: {
        INVOICE: 0,
        PAYMENT: 0,
        TRANSACTION: 0
      } as Record<AuditEntityType, number>,
      uniqueUsers: 0,
      dateRange: null as { earliest: string; latest: string } | null
    };

    if (entries.length === 0) {
      return stats;
    }

    const userIds = new Set<string>();
    let earliest = entries[0].timestamp;
    let latest = entries[0].timestamp;

    entries.forEach(entry => {
      stats.byAction[entry.action]++;
      stats.byEntityType[entry.entityType]++;
      userIds.add(entry.userId);

      if (entry.timestamp < earliest) earliest = entry.timestamp;
      if (entry.timestamp > latest) latest = entry.timestamp;
    });

    stats.uniqueUsers = userIds.size;
    stats.dateRange = { earliest, latest };

    return stats;
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

/**
 * Default singleton instance for convenience
 *
 * Can be used directly throughout the application:
 * ```typescript
 * import { auditTrailService } from './audit/auditTrail';
 *
 * await auditTrailService.log({...});
 * ```
 */
export const auditTrailService = new AuditTrailService();

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Helper function to extract differences between two objects
 * Useful for logging UPDATE operations
 *
 * @param previous Previous state
 * @param current Current state
 * @returns Object containing only changed fields
 */
export function extractChanges(
  previous: Record<string, any>,
  current: Record<string, any>
): { previous: Record<string, any>; current: Record<string, any> } {
  const changes = {
    previous: {} as Record<string, any>,
    current: {} as Record<string, any>
  };

  // Check all keys in current object
  Object.keys(current).forEach(key => {
    if (JSON.stringify(previous[key]) !== JSON.stringify(current[key])) {
      changes.previous[key] = previous[key];
      changes.current[key] = current[key];
    }
  });

  // Check for deleted keys
  Object.keys(previous).forEach(key => {
    if (!(key in current)) {
      changes.previous[key] = previous[key];
      changes.current[key] = undefined;
    }
  });

  return changes;
}

/**
 * Helper to create audit context from browser environment
 *
 * @returns Object with IP address and user agent if available
 */
export function getBrowserContext(): Pick<AuditEntry, 'userAgent'> & { ipAddress?: string } {
  return {
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    // Note: IP address must be provided by backend in real implementation
    ipAddress: undefined
  };
}
